import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Printer, Share2, Film } from "lucide-react";
import QRCodeLib from "qrcode";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSite } from "../context/SiteContext";
import QRCode from "../components/QRCode";

const DOMAIN_URL = "https://letgowelllness.com";

// Generate a QR PNG client-side and trigger a download.
async function downloadQr({ filename, dark, light, size = 1024, margin = 2 }) {
  try {
    const dataUrl = await QRCodeLib.toDataURL(DOMAIN_URL, {
      errorCorrectionLevel: "H",
      margin,
      width: size,
      color: { dark, light },
    });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
    toast.success("QR code downloaded.");
  } catch (e) {
    console.error(e);
    toast.error("Could not generate QR code.");
  }
}

export default function Share() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";

  return (
    <main className="min-h-screen bg-[#FDFBF7] text-[#1C1917] font-sans antialiased" data-testid="share-page">
      <Navbar />

      <section className="pt-48 pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/" data-testid="share-back" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#3A5A40] transition-colors mb-12">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>

          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div>
              <p className="overline text-[#9F5C45] mb-6">Share Let Go Wellness</p>
              <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] mb-8">
                Send this to someone who <em className="italic text-[#3A5A40]">needs</em> us.
              </h1>
              <p className="text-[#57534E] text-base md:text-lg leading-relaxed mb-10">
                Print it. Pin it. Share it. Every QR code links straight to
                <strong className="text-[#1C1917]"> letgowelllness.com</strong> — where they can read about us,
                meet our team, and book a session.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => downloadQr({ filename: "letgo-wellness-qr-sage.png", dark: "#3A5A40", light: "#FDFBF7", size: 1024 })}
                  data-testid="share-dl-sage"
                  className="w-full inline-flex items-center justify-between gap-3 px-6 py-4 btn-primary text-sm tracking-wide"
                >
                  <span className="inline-flex items-center gap-2"><Download className="w-4 h-4" /> Download · Sage on cream (brand)</span>
                  <span className="text-xs opacity-80">PNG · 1024 px</span>
                </button>
                <button
                  onClick={() => downloadQr({ filename: "letgo-wellness-qr-print.png", dark: "#000000", light: "#FFFFFF", size: 1200 })}
                  data-testid="share-dl-print"
                  className="w-full inline-flex items-center justify-between gap-3 px-6 py-4 btn-ghost text-sm tracking-wide"
                >
                  <span className="inline-flex items-center gap-2"><Printer className="w-4 h-4" /> Download · Black on white (print)</span>
                  <span className="text-xs opacity-80">PNG · 1200 px</span>
                </button>
                <button
                  onClick={() => downloadQr({ filename: "letgo-wellness-qr-white.png", dark: "#FFFFFF", light: "#3A5A40", size: 1200 })}
                  data-testid="share-dl-white"
                  className="w-full inline-flex items-center justify-between gap-3 px-6 py-4 btn-ghost text-sm tracking-wide"
                >
                  <span className="inline-flex items-center gap-2"><Download className="w-4 h-4" /> Download · White on sage (dark cards)</span>
                  <span className="text-xs opacity-80">PNG · 1200 px</span>
                </button>
              </div>

              <div className="border-t border-[#E2DCD0] mt-12 pt-8">
                <p className="overline text-[#9F5C45] mb-4 inline-flex items-center gap-2">
                  <Share2 className="w-3.5 h-3.5" /> Or share the link directly
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <code className="bg-[#F4F1EA] px-4 py-3 text-sm text-[#1C1917] flex-1 min-w-[260px]" data-testid="share-link">
                    https://letgowelllness.com
                  </code>
                  <button
                    onClick={() => { navigator.clipboard.writeText("https://letgowelllness.com"); toast.success("Link copied."); }}
                    data-testid="share-copy"
                    className="px-5 py-3 btn-primary text-xs tracking-wide"
                  >
                    Copy
                  </button>
                </div>
                <div className="flex flex-wrap gap-3 mt-6">
                  <a
                    href={`sms:?&body=${encodeURIComponent("I came across this place that might help — " + "https://letgowelllness.com")}`}
                    className="px-5 py-3 btn-ghost text-xs tracking-wide"
                  >
                    Send by text
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent("Let Go Wellness")}&body=${encodeURIComponent("I think this might help — " + "https://letgowelllness.com")}`}
                    className="px-5 py-3 btn-ghost text-xs tracking-wide"
                  >
                    Send by email
                  </a>
                </div>
              </div>
            </div>

            {/* Printable QR card preview */}
            <div className="relative">
              <div className="bg-[#F4F1EA] aspect-[3/4] flex flex-col items-center justify-center p-10 border border-[#E2DCD0]" data-testid="share-card-preview">
                <p className="overline text-[#9F5C45] mb-2">Scan to visit</p>
                <h3 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-1 text-center">{brand}</h3>
                <p className="font-serif italic text-[#3A5A40] text-base mb-8">a quiet space to let go.</p>
                <QRCode
                  value={DOMAIN_URL}
                  dark="#3A5A40"
                  light="#FDFBF7"
                  size={1024}
                  alt="QR code linking to letgowelllness.com"
                  className="w-2/3 max-w-[240px] aspect-square object-contain bg-white p-3"
                />
                <p className="overline text-[#57534E] mt-8 text-center">letgowelllness.com</p>
                <p className="text-xs text-[#57534E] mt-2 text-center">808 Highland Ave · Waterbury, CT · 203-437-8193</p>
              </div>
              <button
                onClick={() => window.print()}
                data-testid="share-print-btn"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 btn-primary text-sm tracking-wide"
              >
                <Printer className="w-4 h-4" /> Print this card
              </button>
            </div>
          </div>

          <div className="border-t border-[#E2DCD0] mt-20 pt-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <div>
                <p className="overline text-[#9F5C45] mb-2">Marketing package</p>
                <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917]">Need print-ready materials?</h2>
                <p className="text-[#57534E] mt-2 max-w-lg">
                  Business cards (Waleska + Max), flyer, social posts and email signatures — all generated from this clinic&apos;s branding.
                </p>
              </div>
              <Link
                to="/marketing"
                data-testid="share-to-marketing"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 btn-primary text-xs tracking-[0.22em] uppercase whitespace-nowrap"
              >
                Open marketing kit
              </Link>
            </div>

            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6">Where it shines</h2>
            <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 text-[#57534E] list-disc list-inside">
              <li>Business cards</li>
              <li>Clinic window decal</li>
              <li>Intake paperwork</li>
              <li>Email signatures</li>
              <li>Instagram bio link</li>
              <li>Community flyers</li>
              <li>Referring physicians&apos; offices</li>
              <li>Wellness fair handouts</li>
              <li>Direct mail postcards</li>
            </ul>
          </div>

          {/* — Video Script Studio tile — */}
          <div className="border-t border-[#E2DCD0] mt-16 pt-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="overline text-[#9F5C45] mb-2 flex items-center gap-2">
                  <Film className="w-3.5 h-3.5" /> Video Script Studio
                </p>
                <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917]">
                  Need a YouTube Short script?
                </h2>
                <p className="text-[#57534E] mt-2 max-w-lg">
                  Ask a question, pick a clinician, get a hook + script + video cues + pull-quote — clinical, warm, and on-brand. Every script saves to your library.
                </p>
              </div>
              <Link
                to="/video"
                data-testid="share-to-video"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 btn-primary text-xs tracking-[0.22em] uppercase whitespace-nowrap"
              >
                <Film className="w-4 h-4" /> Open video studio
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
